import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

import SaveListDrawer from "./SaveListDrawer";
import { ListEntities } from "./SaveToLists";

type DrawerData = {
  entityType: ListEntities;
  entityIds: string[];
};

type SaveListDrawerContextValue = {
  openListDrawer: (data: DrawerData) => void;
  closeListDrawer: () => void;
};

const SaveListDrawerContext = createContext<SaveListDrawerContextValue | null>(null);

export function useSaveListDrawer() {
  const ctx = useContext(SaveListDrawerContext);
  if (!ctx) throw new Error("useSaveListDrawer must be used within SaveListDrawerProvider");
  return ctx;
}

/**
 * Provides a single, shared SaveListDrawer instance for all consumers.
 *
 * This avoids rendering one drawer per table row by centralising drawer state
 * and dynamically supplying the active entity data when a row requests it.
 * Improves performance and ensures only one drawer can be open at a time.
 */
export function SaveListDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<DrawerData | null>(null);

  const openListDrawer = useCallback((next: DrawerData) => {
    setData(next);
    setIsOpen(true);
  }, []);

  const closeListDrawer = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const value = useMemo(
    () => ({ openListDrawer, closeListDrawer }),
    [openListDrawer, closeListDrawer],
  );

  return (
    <SaveListDrawerContext.Provider value={value}>
      {children}

      {/* Single shared drawer instance */}
      {data && (
        <SaveListDrawer
          {...data}
          isOpen={isOpen}
          onOpenChange={(open) => {
            setIsOpen(open);
            if (!open) setData(null);
          }}
        />
      )}
    </SaveListDrawerContext.Provider>
  );
}
